import { useCallback, useEffect, useState } from "react";
import { createInvite, emailInvite, fetchInvites, probeEmail, revokeInvite } from "../lib/api";
import { Check, Copy, Link2, Mail, Send, X } from "lucide-react";

const STATUS = {
  open: "text-zinc-100",
  used: "text-zinc-500",
  expired: "text-zinc-600",
  revoked: "text-zinc-700",
};

/** Sealed one-time links. The link authorises; Google still identifies the subject. */
export const Invites = () => {
  const [d, setD] = useState(null);
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(7);
  const [fresh, setFresh] = useState(null);
  const [copied, setCopied] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState("");
  const [probe, setProbe] = useState(null);

  const load = useCallback(() => fetchInvites().then(setD).catch(() => {}), []);

  useEffect(() => {
    load();
  }, [load]);

  const urlFor = (token) => `${window.location.origin}/i/${token}`;

  const create = async () => {
    setBusy(true);
    setMsg("");
    try {
      const res = await createInvite(label.trim() || null, Number(days) || 7, email.trim() || null);
      setFresh(res.invite);
      setLabel("");
      setMsg(
        res.delivery?.sent_at
          ? `emailed to ${res.delivery.to}${res.delivery.fell_back ? " (via the fallback sender — your own domain refused it)" : ""}`
          : "link minted — nothing emailed"
      );
      setEmail("");
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "could not mint a link");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const copy = async (token) => {
    await navigator.clipboard.writeText(urlFor(token));
    setCopied(token);
    setTimeout(() => setCopied(""), 1800);
  };

  return (
    <div data-testid="admin-invites" className="space-y-10">
      <div>
        <p className="label">Sealed invitations</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          Send a subject a private link instead of asking them to find the entrance. Each link
          works once, expires, and is what admits them — without one, a new account cannot be
          opened at all, by Google or by email. The code underneath is the same invitation for
          anyone who would rather type it in.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <input
          data-testid="invite-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") create();
          }}
          placeholder="who is this for — e.g. Sarah, Ops Director"
          className="min-w-[16rem] flex-1 border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] text-zinc-100 outline-none placeholder:text-zinc-700"
        />
        <input
          data-testid="invite-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") create();
          }}
          placeholder="email it to (optional)"
          className="min-w-[16rem] flex-1 border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] text-zinc-100 outline-none placeholder:text-zinc-700"
        />
        <input
          data-testid="invite-days-input"
          type="number"
          min={1}
          max={90}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="w-24 border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-3 font-data text-[12px] text-zinc-100 outline-none"
        />
        <span className="label pb-3">days</span>
        <button
          data-testid="invite-create-btn"
          disabled={busy}
          onClick={create}
          className="btn-stark flex items-center gap-2 !py-3"
        >
          {email.trim() ? (
            <>
              <Send size={11} strokeWidth={1.8} /> Mint and send
            </>
          ) : (
            <>
              <Link2 size={11} strokeWidth={1.8} /> Mint link
            </>
          )}
        </button>
      </div>

      {fresh && (
        <div data-testid="invite-fresh" className="border border-zinc-600 px-5 py-5">
          <p className="label">New link{fresh.label ? ` · ${fresh.label}` : ""}</p>
          <p className="mt-3 break-all font-data text-[11px] leading-relaxed text-zinc-200">
            {urlFor(fresh.token)}
          </p>
          <p className="mt-4 font-data text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            or the code, typed in by hand
          </p>
          <p
            data-testid="invite-fresh-code"
            className="mt-2 font-data text-xl tracking-[0.3em] text-zinc-100"
          >
            {fresh.code}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              data-testid="invite-copy-fresh"
              onClick={() => copy(fresh.token)}
              className="btn-stark flex items-center gap-2 !px-3 !py-2"
            >
              {copied === fresh.token ? (
                <>
                  <Check size={11} strokeWidth={2} /> Copied
                </>
              ) : (
                <>
                  <Copy size={11} strokeWidth={1.8} /> Copy link
                </>
              )}
            </button>
            <span className="font-data text-[10px] text-zinc-600">
              single use · expires {fresh.expires_at?.slice(0, 10)}
            </span>
          </div>
        </div>
      )}

      <div>
        <p className="label">Issued · {d?.invites?.length ?? 0}</p>
        <ul className="mt-4" data-testid="invite-list">
          {(d?.invites || []).map((i) => (
            <li
              key={i.id}
              data-testid={`invite-row-${i.id}`}
              className="flex flex-wrap items-center gap-3 border-b border-[var(--line)] py-3.5"
            >
              <span
                className={`font-data text-[10px] uppercase tracking-[0.16em] ${STATUS[i.status]}`}
              >
                {i.status}
              </span>
              <span className="text-sm text-zinc-200">{i.label || "unlabelled"}</span>
              {i.code && i.status === "open" && (
                <span
                  data-testid={`invite-code-${i.id}`}
                  className="font-data text-[11px] tracking-[0.22em] text-zinc-300"
                >
                  {i.code}
                </span>
              )}
              <span className="font-data text-[10px] text-zinc-600">
                expires {i.expires_at?.slice(0, 10)}
                {i.used_email ? ` · taken by ${i.used_email}` : ""} · by {i.created_by}
              </span>
              {i.delivery && (
                <span
                  data-testid={`invite-delivery-${i.id}`}
                  className="flex items-center gap-1.5 font-data text-[10px] text-zinc-500"
                >
                  <Mail size={10} strokeWidth={1.6} />
                  {i.delivery.sent_at
                    ? `sent to ${i.delivery.to}${i.delivery.fell_back ? " · fallback sender" : ""}`
                    : `would not send to ${i.delivery.to}`}
                </span>
              )}
              <div className="ml-auto flex items-center gap-4">
                {i.status === "open" && (
                  <>
                    <button
                      data-testid={`invite-copy-${i.id}`}
                      onClick={() => copy(i.token)}
                      className="label flex items-center gap-1.5 hover:text-zinc-100"
                    >
                      {copied === i.token ? (
                        <>
                          <Check size={11} strokeWidth={2} /> copied
                        </>
                      ) : (
                        <>
                          <Copy size={11} strokeWidth={1.7} /> copy
                        </>
                      )}
                    </button>
                    <button
                      data-testid={`invite-email-${i.id}`}
                      disabled={resending === i.id}
                      onClick={async () => {
                        const to =
                          (i.delivery || {}).to ||
                          window.prompt(`Email this invitation to:`, "")?.trim();
                        if (!to) return;
                        setResending(i.id);
                        setMsg("");
                        try {
                          const r = await emailInvite(i.id, to);
                          setMsg(`sent to ${r.delivery.to}`);
                        } catch (e) {
                          setMsg(e?.response?.data?.detail || "could not send it");
                        } finally {
                          setResending("");
                          await load();
                        }
                      }}
                      className="label flex items-center gap-1.5 hover:text-zinc-100"
                    >
                      <Mail size={11} strokeWidth={1.7} />
                      {resending === i.id ? "sending" : i.delivery ? "re-send" : "email it"}
                    </button>
                    <button
                      data-testid={`invite-revoke-${i.id}`}
                      disabled={busy}
                      onClick={async () => {
                        await revokeInvite(i.id);
                        await load();
                        setMsg("link revoked");
                      }}
                      className="label flex items-center gap-1.5 hover:text-zinc-100"
                    >
                      <X size={11} strokeWidth={1.8} /> revoke
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
          {(d?.invites || []).length === 0 && (
            <p className="font-data text-[11px] text-zinc-600">
              No links issued yet. Existing accounts can still sign in; new ones cannot without a
              link.
            </p>
          )}
        </ul>
      </div>

      {msg && (
        <p data-testid="invite-note" className="font-data text-[11px] text-zinc-400">
          — {msg}
        </p>
      )}

      <div className="border-t border-[var(--line)] pt-8">
        <p className="label">Is outbound email working?</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          Sends a test message and tells you exactly what the provider said. If your own domain is
          not verified yet, invitations still go out through a fallback sender — this is how you
          find out which one is carrying them.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            data-testid="email-probe-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="send a test to…"
            className="min-w-[16rem] border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] text-zinc-100 outline-none placeholder:text-zinc-700"
          />
          <button
            data-testid="email-probe-btn"
            disabled={!email.trim()}
            onClick={async () => {
              setProbe(null);
              try {
                setProbe(await probeEmail(email.trim()));
              } catch (e) {
                setProbe({ error: e?.response?.data?.detail || "the test itself failed" });
              }
            }}
            className="btn-stark flex items-center gap-2 !py-3 disabled:opacity-40"
          >
            <Send size={11} strokeWidth={1.8} /> Test
          </button>
        </div>
        {probe && (
          <div
            data-testid="email-probe-result"
            className="mt-5 border border-[var(--line)] px-5 py-4"
          >
            {probe.error ? (
              <p className="font-data text-[11px] text-zinc-300">— {probe.error}</p>
            ) : (
              <>
                <p className="font-data text-[11px] text-zinc-200">
                  {probe.domain_verified
                    ? `Delivered from ${probe.from}. Your domain is verified.`
                    : `Your domain refused it. Sent through the fallback sender instead.`}
                </p>
                <p className="mt-2 font-data text-[10px] leading-relaxed text-zinc-600">
                  to {probe.to} · replies to {probe.reply_to} · id{" "}
                  {probe.resend?.id || probe.managed?.id || "—"}
                </p>
                {probe.advice && (
                  <p className="mt-3 max-w-2xl text-xs leading-relaxed text-zinc-400">
                    {probe.advice}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invites;
